import React, { useMemo } from 'react';
import * as FiIcons from 'react-icons/fi';
import { Query } from '../../types/Query';
import { QueryLane, isBookedQueryStatus, isClosedQueryStatus } from '../../utils/queryStatus';
import { buildWebsiteDescription } from '../../utils/websiteQueryDescription';
import { isVendorRfqEnabled } from '../../utils/vendorRfq';

const FaEye = FiIcons.FiEye as unknown as React.FC;
const FaPlus = FiIcons.FiPlus as unknown as React.FC;
const FaShare = FiIcons.FiShare as unknown as React.FC;
const FaAssign = FiIcons.FiUserPlus as unknown as React.FC;

type QueryCardProps = {
  query: Query;
  lane: QueryLane;
  assigneeName: string;
  role?: string;
  variant?: 'card' | 'modal';
  onOpen?: (query: Query) => void;
  nextFollowupLabel?: string;
  formatDate: (value: string) => string;
  onOpenCustomer: (customerId?: number) => void;
  onAddFollowup: (queryId: number) => void;
  onShareQuotation: (query: Query) => void;
  onViewHistory: (queryId: number) => void;
  onAssign: (queryId: number) => void;
  onVendorRfq?: (queryId: number) => void;
  onDragStart?: (queryId: number, ev: React.DragEvent<HTMLDivElement>) => void;
  onDragOver?: (ev: React.DragEvent<HTMLDivElement>) => void;
  onDrop?: (lane: QueryLane, ev: React.DragEvent<HTMLDivElement>, beforeId?: number | null) => void;
};

const QueryCard: React.FC<QueryCardProps> = ({
  query,
  lane = 'NEW',
  assigneeName,
  role,
  variant = 'card',
  onOpen,
  nextFollowupLabel,
  formatDate,
  onOpenCustomer,
  onAddFollowup,
  onShareQuotation,
  onViewHistory,
  onAssign,
  onVendorRfq,
  onDragStart,
  onDragOver,
  onDrop,
}) => {
  const shareDisabled = isBookedQueryStatus(query.status) || isClosedQueryStatus(query.status);
  const shareTitle = shareDisabled ? 'Disabled for Booked/Closed queries' : 'Share Quotation';
  const showShare = role !== 'sales';
  const vendorRfqEnabled = isVendorRfqEnabled();
  const structuredDescription = useMemo(() => {
    if (!query.description) return null;
    return buildWebsiteDescription(query.description);
  }, [query.description]);

  const copyToClipboard = async (value: string) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        return;
      }
      const el = document.createElement('textarea');
      el.value = value;
      el.setAttribute('readonly', 'true');
      el.style.position = 'absolute';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    } catch {
      // ignore copy failures
    }
  };

  const isCardVariant = variant === 'card';
  const showDrag = isCardVariant && Boolean(onDragStart && onDragOver && onDrop);
  const isClosedLane = lane === 'CLOSED';
  const updatedAt = query.updatedAt || query.updated_at || query.createdAt || '';
  const updatedAtDate = updatedAt ? new Date(updatedAt) : null;
  const isBookedLane = lane === 'BOOKED';
  const isRecentUpdate = Boolean(
    !isBookedLane && !isClosedLane &&
    updatedAtDate && !Number.isNaN(updatedAtDate.getTime()) &&
    (Date.now() - updatedAtDate.getTime()) <= 24 * 60 * 60 * 1000
  );
  const updatedAtIst = updatedAtDate && !Number.isNaN(updatedAtDate.getTime())
    ? updatedAtDate.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
    : '—';

  const showFullDescription = variant === 'modal';
  const descriptionText = query.description || '';
  const roomSummary = useMemo(() => {
    if (Array.isArray(query.rooms) && query.rooms.length > 0) {
      const roomCount = query.rooms.length;
      const totalAdults = query.rooms.reduce(
        (sum, room) => sum + Number(room.Adults ?? room.adults ?? 0),
        0
      );
      const totalChildren = query.rooms.reduce(
        (sum, room) => sum + Number(room.Children ?? room.children ?? 0),
        0
      );
      const roomLabel = `${roomCount} Room${roomCount === 1 ? '' : 's'}`;
      const occupancyParts = [
        totalAdults > 0 ? `${totalAdults} Adult${totalAdults === 1 ? '' : 's'}` : null,
        totalChildren > 0 ? `${totalChildren} Child${totalChildren === 1 ? '' : 'ren'}` : null,
      ].filter(Boolean);
      return occupancyParts.length > 0 ? `${roomLabel} (${occupancyParts.join(', ')})` : roomLabel;
    }

    if (typeof query.pax === 'number' && query.pax > 0) {
      return `${query.pax} Pax`;
    }

    return '—';
  }, [query.rooms, query.pax]);

  return (
    <div
      className={`query-card compact ${variant === 'modal' ? 'query-card-modal' : ''} ${isRecentUpdate ? 'query-card--recent' : ''} ${isClosedLane ? 'query-card--closed' : ''} ${isBookedLane ? 'query-card--booked' : ''}`}
      draggable={showDrag}
      onClick={() => onOpen?.(query)}
      onDragStart={showDrag ? (e) => onDragStart?.(query.id, e) : undefined}
      onDragOver={showDrag ? (e) => onDragOver?.(e) : undefined}
      onDrop={showDrag ? (e) => onDrop?.(lane, e, query.id) : undefined}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onKeyDown={(e) => {
        if (!onOpen) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(query);
        }
      }}
    >
      <div className="qc-header">
        <div className="qc-title-row">
          <span
            className="qc-customer-name clickable-name"
            onClick={(e) => {
              e.stopPropagation();
              onOpenCustomer(query.customer?.id);
            }}
          >
            {query.customer?.name || 'Unknown Customer'}
          </span>
          {query.referenceId && (
            <span
              className="qc-reference-id"
              title="Copy reference ID"
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(query.referenceId as string);
              }}
            >
              {query.referenceId}
            </span>
          )}
        </div>
      </div>
      <div className="qc-meta-list">
        <div className="qc-meta-row">
          <span className="qc-meta-label">Type:</span>
          <span>{query.type || '—'}</span>
        </div>
        <div className="qc-meta-row">
          <span className="qc-meta-label">Location:</span>
          <span>{query.location || '—'}</span>
        </div>
        <div className="qc-meta-row">
          <span className="qc-meta-label">Rooms:</span>
          <span>{roomSummary}</span>
        </div>
        <div className="qc-meta-row">
          <span className="qc-meta-label">Assignee:</span>
          <span>{assigneeName}</span>
        </div>
        {nextFollowupLabel && (
          <div className="qc-meta-row">
            <span className="qc-meta-label">Next Follow-up:</span>
            <span>{nextFollowupLabel}</span>
          </div>
        )}
        <div className="qc-meta-row">
          <span className="qc-meta-label">Travel Date:</span>
          <span>{formatDate(query.queryDate) || '—'}</span>
        </div>
        <div className="qc-meta-row">
          <span className="qc-meta-label">Last Updated:</span>
          <span>{updatedAtIst} IST</span>
        </div>
      </div>
      {query.description && (
        <div
          className="qc-description"
          title={variant === 'modal' ? undefined : query.description}
        >
          {structuredDescription ? (
            <>
              <div className="qc-description-summary">{structuredDescription.summary}</div>
              {structuredDescription.tooltipRows.length > 0 && (
                <div className={`qc-description-tooltip ${showFullDescription ? 'qc-description-tooltip-open' : ''}`}>
                  <table className="qc-description-table">
                    <tbody>
                      {structuredDescription.tooltipRows.map((row, idx) => (
                        <tr key={`${query.id}-row-${idx}`}>
                          <th>{row.label}</th>
                          <td>
                            {row.values.map((value, vIdx) => (
                              <div key={`${query.id}-row-${idx}-value-${vIdx}`} className="qc-description-line">
                                {value}
                              </div>
                            ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : (
            showFullDescription
              ? descriptionText
              : (descriptionText.length > 120
                ? `${descriptionText.substring(0, 120)}...`
                : descriptionText)
          )}
        </div>
      )}
      <div className="qc-actions">
        {vendorRfqEnabled && onVendorRfq && (
          <button
            className="qc-action-btn"
            title="Vendor RFQ"
            onClick={(e) => {
              e.stopPropagation();
              onVendorRfq(query.id);
            }}
          >
            <FaPlus /> <span className="qc-action-label">Vendor RFQ</span>
          </button>
        )}
        <button
          className="qc-action-btn"
          title="Add FollowUp"
          onClick={(e) => {
            e.stopPropagation();
            onAddFollowup(query.id);
          }}
        >
          <FaPlus /> <span className="qc-action-label">Add FollowUp</span>
        </button>
        {showShare && (
          <button
            className="qc-action-btn"
            title={shareTitle}
            onClick={(e) => {
              e.stopPropagation();
              onShareQuotation(query);
            }}
            disabled={shareDisabled}
          >
            <FaShare /> <span className="qc-action-label">Share Quotation</span>
          </button>
        )}
        <button
          className="qc-action-btn"
          title="View History"
          onClick={(e) => {
            e.stopPropagation();
            onViewHistory(query.id);
          }}
        >
          <FaEye /> <span className="qc-action-label">History</span>
        </button>
        <button
          className="qc-action-btn"
          title={query.assignedTo ? 'ReAssign' : 'Assign'}
          onClick={(e) => {
            e.stopPropagation();
            onAssign(query.id);
          }}
        >
          <FaAssign /> <span className="qc-action-label">Assign</span>
        </button>
      </div>
    </div>
  );
};

export default QueryCard;
